import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useTranslation } from "react-i18next";

function formatUnitPrice(option, currencySymbol) {
  const value = Number(option?.unit_price);
  if (!Number.isFinite(value) || value <= 0) return null;
  const kind = String(option?.unit_price_kind || "").toLowerCase();
  if (kind === "piece") return `${currencySymbol}${value.toFixed(2)}/pc`;
  if (kind === "liter" || kind === "l") return `${currencySymbol}${value.toFixed(2)}/L`;
  return `${currencySymbol}${value.toFixed(2)}/kg`;
}

function buildStoreLabel(option) {
  if (option?.store_chain && option?.store_name && option.store_chain !== option.store_name) {
    return `${option.store_chain} • ${option.store_name}`;
  }
  return option?.store_chain || option?.store_name || "";
}

export default function ProductSelectionModal({
  open,
  onClose,
  genericProducts = [],
  variants = [],
  mode = "genericThenVariants",
  lockedGenericProductId,
  lockedGenericProductName = "",
  selectedGenericId,
  currentVariantId,
  searchValue,
  onSearchChange,
  onSelectGeneric,
  onSelectVariant,
  loadingGenericProducts = false,
  loadingVariants = false,
  submittingSelection = false,
  error = "",
  title,
  emptyProductsLabel,
  emptyVariantsLabel,
}) {
  const { t } = useTranslation();
  const [pendingVariantId, setPendingVariantId] = useState(currentVariantId || "");
  const variantsOnly = mode === "variantsOnly";
  const dialogTitle = title || t("product_selection_title");
  const noProductsLabel = emptyProductsLabel || t("no_generic_products_found");
  const noVariantsLabel = emptyVariantsLabel || t("no_store_variants_found");

  useEffect(() => {
    if (open) {
      setPendingVariantId(currentVariantId || "");
    }
  }, [open, currentVariantId]);

  useEffect(() => {
    if (open && variantsOnly && lockedGenericProductId) {
      onSelectGeneric?.(lockedGenericProductId);
    }
  }, [open, variantsOnly, lockedGenericProductId, onSelectGeneric]);

  const selectedGeneric = useMemo(
    () => genericProducts.find((item) => String(item.id) === String(selectedGenericId)) || null,
    [genericProducts, selectedGenericId]
  );

  const handleVariantClick = async (variantId) => {
    setPendingVariantId(variantId);
    await onSelectVariant?.(variantId);
  };

  return (
    <Dialog open={open} onClose={submittingSelection ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ minHeight: 420 }}>
          {!variantsOnly ? (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                size="small"
                value={searchValue}
                onChange={(event) => onSearchChange?.(event.target.value)}
                placeholder={t("search_generic_products")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1.5 }}
              />
              {loadingGenericProducts ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <List dense sx={{ maxHeight: 320, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                  {genericProducts.length ? genericProducts.map((product) => (
                    <ListItemButton
                      key={product.id}
                      selected={String(product.id) === String(selectedGenericId)}
                      onClick={() => onSelectGeneric?.(product.id)}
                    >
                      <ListItemText
                        primary={product.name}
                        secondary={product.name_el && product.name_en && product.name_el !== product.name_en ? `${product.name_el} / ${product.name_en}` : null}
                      />
                    </ListItemButton>
                  )) : (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      {noProductsLabel}
                    </Typography>
                  )}
                </List>
              )}
            </Box>
          ) : null}

          <Box sx={{ flex: variantsOnly ? 1 : 1.35, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {variantsOnly
                ? lockedGenericProductName || selectedGeneric?.name || t("select_a_product")
                : selectedGeneric?.name || t("select_generic_product")}
            </Typography>
            {loadingVariants ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List sx={{ maxHeight: 360, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                {variants.length ? variants.map((variant) => {
                  const packageText = variant.package_size_text || t("package_size_unavailable");
                  const price = Number(variant.package_price ?? variant.price ?? 0) || 0;
                  const unitPrice = formatUnitPrice(variant, variant.currency === "USD" ? "$" : "€");
                  const storeLabel = buildStoreLabel(variant);
                  const primary = variant.brand_name || variant.title || selectedGeneric?.name || t("variant");
                  const groupedCount = Number(variant.duplicate_count || 0);
                  return (
                    <ListItemButton
                      key={variant.id || variant.product_variant_id}
                      selected={String(pendingVariantId) === String(variant.id || variant.product_variant_id)}
                      disabled={submittingSelection}
                      onClick={() => handleVariantClick(variant.id || variant.product_variant_id)}
                      sx={{ alignItems: "flex-start", py: 1.5 }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                              {primary}
                            </Typography>
                            {storeLabel ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <StorefrontIcon sx={{ fontSize: 14 }} />
                                {storeLabel}
                              </Typography>
                            ) : null}
                          </Stack>
                        }
                        secondary={
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                            <Typography variant="body2">{`${variant.currency === "USD" ? "$" : "€"}${price.toFixed(2)}`}</Typography>
                            <Typography variant="body2" color="text.secondary">{packageText}</Typography>
                            {unitPrice ? (
                              <Typography variant="body2" color="text.secondary">{unitPrice}</Typography>
                            ) : null}
                            {groupedCount > 1 ? (
                              <Typography variant="body2" color="text.secondary">
                                {t("grouped_nearby_offers", { count: groupedCount })}
                              </Typography>
                            ) : null}
                          </Stack>
                        }
                      />
                    </ListItemButton>
                  );
                }) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {noVariantsLabel}
                  </Typography>
                )}
              </List>
            )}
          </Box>
        </Stack>
        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submittingSelection}>{t("close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
